/**
 * wrapper for the fs / fs-extra module
 * this allows us to customise the behaviour of fs function across the application,
 * In particular it handles certain user-interactions (file busy, permissions, ...) in a uniform
 * way.
 * The api should remain compatible with fs-extra, but extensions can be made
 * Notable behaviour changes:
 * - common async functions now retrieve a backtrace before calling, so that on error
 *   they can provide a useful backtrace to where the function was called
 *   (for many error cases the original function didn't have a stack trace in the first place)
 * - retrying on functions that commonly fail temporarily due to external applications
 *   (virus scanners, functions called from vortex) locking files.
 * - ignoring ENOENT error when deleting a file.
 */
import { getErrorCode, getErrorMessageOrDefault, getErrorNativeCode, isErrorWithSystemCode, unknownToError, } from "@vortex/shared";
import PromiseBB from "bluebird";
import * as fs from "fs-extra";
import { decode } from "iconv-lite";
import JsonSocket from "json-socket";
import * as _ from "lodash";
import * as net from "net";
import * as path from "path";
import rimraf from "rimraf";
import { generate as shortid } from "shortid";
import * as tmp from "tmp";
import { ProcessCanceled, SelfCopyCheckError, UserCanceled, } from "./CustomErrors";
import { runElevated } from "./elevated";
import { createErrorReport } from "./errorHandling";
import lazyRequire from "./lazyRequire";
import { log } from "./log";
import { decodeSystemError } from "./nativeErrors";
import { restackErr, truthy } from "./util";
const permission = lazyRequire(() => require("permissions"));
const wholocks = lazyRequire(() => require("wholocks"));
const showMessageBox = async (options) => {
    return window.api.dialog.showMessageBox(options);
};
export { constants, Stats, WriteStream } from "fs";
// simple re-export of functions we don't touch (yet)
export { accessSync, appendFileSync, closeSync, createReadStream, createWriteStream, linkSync, openSync, readdirSync, readFileSync, statSync, symlinkSync, watch, writeFileSync, writeSync, } from "original-fs";
let tFunction = (input) => input;
export function setTFunction(tFunc) {
    tFunction = tFunc;
}
const NUM_RETRIES = 5;
const RETRY_DELAY_MS = 100;
const RETRY_ERRORS = new Set([
    "EPERM",
    "EBUSY",
    "EIO",
    "EBADF",
    "ENOTEMPTY",
    "EMFILE",
    "UNKNOWN",
]);
// Tracks paths where elevated permissions have been successfully granted,
// so we don't show the unlock dialog repeatedly for child paths or
// loop infinitely when permissions don't stick.
// Entries are removed when they prove ineffective (isPathAlreadyUnlocked),
// keeping the set small over time.
const elevatedUnlockPaths = new Set();
function normalizePath(filePath) {
    return path.normalize(filePath).toLowerCase();
}
function isPathAlreadyUnlocked(filePath) {
    const normalized = normalizePath(filePath);
    if (elevatedUnlockPaths.has(normalized)) {
        // Remove the entry: if we already unlocked this exact path and the
        // operation still fails, the unlock didn't help. Removing it ensures
        // future EPERM errors on this path will show the dialog again.
        elevatedUnlockPaths.delete(normalized);
        return true;
    }
    return false;
}
function isChildOfUnlockedPath(filePath) {
    const normalized = normalizePath(filePath);
    let found = false;
    elevatedUnlockPaths.forEach((unlocked) => {
        if (normalized.startsWith(unlocked + path.sep)) {
            found = true;
        }
    });
    return found;
}
function recordUnlockedPath(filePath) {
    elevatedUnlockPaths.add(normalizePath(filePath));
}
const simfail = process.env.SIMULATE_FS_ERRORS === "true"
    ? (func) => {
        if (Math.random() < 0.25) {
            const code = Math.random() < 0.33
                ? "EBUSY"
                : Math.random() < 0.5
                    ? "EIO"
                    : "UNKNOWN";
            const res = new Error(`fake error ${code}`);
            if (code === "UNKNOWN") {
                res["nativeCode"] = 21;
            }
            res.code = code;
            res.path = "foobar file";
            return PromiseBB.reject(res);
        }
        else {
            return func();
        }
    }
    : (func) => func();
function nospcQuery() {
    const options = {
        title: "Disk full",
        message: `Operation can't continue because the disk is full. ` +
            "Please free up some space and click retry. Cancelling the transfer operation " +
            "at this point will remove any changes and revert back to the previous state.",
        buttons: ["Cancel", "Retry"],
        type: "warning",
        noLink: true,
    };
    return PromiseBB.resolve(showMessageBox(options)).then((result) => result.response === 0
        ? PromiseBB.reject(new UserCanceled())
        : PromiseBB.resolve(true));
}
function ioQuery() {
    const options = {
        title: "I/O Error",
        message: "Disk access failed repeatedly. " +
            "If this is a removable disk (like a network or external drive), please ensure " +
            "it's connected. Otherwise this may indicate filesystem corruption, you may " +
            "want to run chkdsk or similar software to scan for problems.",
        buttons: ["Cancel", "Retry"],
        type: "warning",
        noLink: true,
    };
    return PromiseBB.resolve(showMessageBox(options)).then((result) => result.response === 0
        ? PromiseBB.reject(new UserCanceled())
        : PromiseBB.resolve(true));
}
function unlockConfirm(filePath) {
    if (!truthy(filePath)) {
        return PromiseBB.resolve(false);
    }
    let processes = [];
    try {
        processes = wholocks.default(filePath);
    }
    catch (err) {
        log("warn", "failed to determine list of processes locking file", {
            filePath,
            error: getErrorMessageOrDefault(err),
        });
    }
    const baseMessage = processes.length === 0
        ? `Vortex needs to access "${filePath}" but doesn't have permission to.`
        : `Vortex needs to access "${filePath}" but it either has too restrictive ` +
            "permissions or is locked by another process.";
    const buttons = ["Cancel", "Retry"];
    if (processes.length === 0) {
        buttons.push("Give permission");
    }
    const options = {
        title: "Access denied",
        message: baseMessage +
            " If your account has admin rights Vortex can try to unlock the file for you.",
        detail: processes.length === 0
            ? undefined
            : "Please close the following applications and retry:\n" +
                processes.map((proc) => `${proc.appName} (${proc.pid})`).join("\n"),
        buttons,
        type: "warning",
        noLink: true,
    };
    return PromiseBB.resolve(showMessageBox(options)).then((result) => result.response === 0
        ? PromiseBB.reject(new UserCanceled())
        : PromiseBB.resolve(result.response === 2));
}
function elevatedUnlock(unlockPath, filePath, originalError) {
    // Record the path immediately (before the async elevated call) so that
    // concurrent operations on child paths see it and skip the dialog
    recordUnlockedPath(unlockPath);
    const userId = permission.getUserId();
    return elevated((ipcPath, req) => {
        return req("permissions").allow(unlockPath, userId, "rwx", {
            recursive: true,
        });
    }, { unlockPath, userId })
        .then(() => true)
        .catch((elevatedErr) => {
        if (elevatedErr instanceof UserCanceled ||
            elevatedErr.message.indexOf("The operation was canceled by the user") !== -1) {
            return Promise.reject(new UserCanceled());
        }
        // if elevation failed, return the original error because the one from
        // elevate - while interesting as well - would make error handling too complicated
        log("error", "failed to acquire permission", {
            filePath,
            error: elevatedErr.message,
        });
        return Promise.reject(originalError);
    });
}
function unknownErrorRetry(filePath, err, stackErr) {
    if (filePath === undefined) {
        // unfortunately these error message don't necessarily contain the filename
        filePath = "<filename unknown>";
    }
    const options = {
        title: "Unknown error",
        message: `The operating system has reported an error without details when accessing "${filePath}" ` +
            "This is usually due the user's environment and not a bug in Vortex.\n" +
            "Please diagnose your environment and then retry",
        type: "warning",
        noLink: true,
    };
    if (![255, 362, 383, 388, 390, 395, 396, 404].includes(err["nativeCode"])) {
        options.detail =
            "Possible error causes:\n" +
                `1. "${filePath}" is a removable, possibly network drive which has been disconnected.\n` +
                "2. An External application has interfered with file operations " +
                "(Anti-virus, Disk Management Utility, Virus)\n";
    }
    const decoded = decodeSystemError(err, filePath);
    if (decoded !== undefined) {
        options.title = decoded.title;
        options.message = tFunction(decoded.message, { replace: { filePath } });
    }
    if (decoded?.rethrowAs === undefined) {
        options.buttons = ["Cancel", "Retry"];
    }
    else {
        options.message +=
            "\n\nYou can try continuing but you do so at your own risk.";
        options.buttons = ["Cancel", "Ignore", "Retry"];
    }
    return PromiseBB.resolve(showMessageBox(options)).then((result) => {
        const choice = result.response;
        if (options.buttons[choice] === "Cancel and Report") {
            // we're reporting this to collect a list of native errors and provide better error
            // message
            const nat = err["nativeCode"];
            createErrorReport("Unknown error", {
                message: `Windows System Error (${nat})`,
                stack: restackErr(err, stackErr).stack,
                path: filePath,
            }, {}, undefined);
            return PromiseBB.reject(new UserCanceled());
        }
        switch (options.buttons[choice]) {
            case "Retry":
                return PromiseBB.resolve(true);
            case "Ignore": {
                err["code"] = decoded?.rethrowAs ?? "UNKNOWN";
                err["allowReport"] = false;
                return PromiseBB.reject(err);
            }
        }
        return PromiseBB.reject(new UserCanceled());
    });
}
function busyRetry(filePath) {
    if (filePath === undefined) {
        filePath = "<filename unknown>";
    }
    let processes = [];
    try {
        processes = wholocks.default(filePath);
    }
    catch (err) {
        log("warn", "failed to determine list of processes locking file", {
            filePath,
            error: getErrorMessageOrDefault(err),
        });
    }
    const options = {
        title: "File busy",
        message: `Vortex needs to access "${filePath}" but it's open in another application. ` +
            "Please close the file in all other applications and then retry.",
        detail: processes.length > 0
            ? "Please close the following applications and retry:\n" +
                processes.map((proc) => `${proc.appName} (${proc.pid})`).join("\n")
            : undefined,
        buttons: ["Cancel", "Retry"],
        type: "warning",
        noLink: true,
    };
    return PromiseBB.resolve(showMessageBox(options)).then((result) => result.response === 0
        ? PromiseBB.reject(new UserCanceled())
        : PromiseBB.resolve(true));
}
function errorRepeat(error, filePath, retries, stackErr, showDialogCallback, options) {
    if (retries > 0 &&
        (RETRY_ERRORS.has(error.code) ||
            (options?.extraRetryErrors || []).includes(error.code))) {
        // retry these errors without query for a few times
        return PromiseBB.delay(retries === 1 ? 1000 : 100).then(() => PromiseBB.resolve(true));
    }
    if (showDialogCallback !== undefined && !showDialogCallback()) {
        return PromiseBB.resolve(false);
    }
    // system error code 1224 means there is a user-mapped section open in the file
    if (error.code === "EBUSY" ||
        error["nativeCode"] === 1224 ||
        (error.code === "ENOTEMPTY" && options?.enotempty)) {
        return busyRetry(filePath);
    }
    else if (error.code === "ENOSPC") {
        return nospcQuery();
    }
    else if (["EBADF", "EIO"].includes(error.code)) {
        return ioQuery();
    }
    else if (error.code === "EPERM") {
        let unlockPath = filePath;
        return PromiseBB.resolve(fs.stat(unlockPath))
            .catch((statErr) => {
            const code = getErrorCode(statErr);
            if (code === "ENOENT") {
                unlockPath = path.dirname(filePath);
                return PromiseBB.resolve();
            }
            else {
                return PromiseBB.reject(statErr);
            }
        })
            .then(() => {
            // If we already granted elevated permissions on this exact path and the
            // operation still fails, the permission change didn't help.
            // Stop retrying to avoid an infinite dialog loop.
            if (isPathAlreadyUnlocked(unlockPath)) {
                return PromiseBB.resolve(false);
            }
            // If this path is under a directory where permissions were already
            // granted recursively, auto-grant without showing the dialog again
            if (isChildOfUnlockedPath(unlockPath)) {
                return elevatedUnlock(unlockPath, filePath, error);
            }
            return unlockConfirm(unlockPath).then((doUnlock) => {
                if (doUnlock) {
                    return elevatedUnlock(unlockPath, filePath, error);
                }
                else {
                    return PromiseBB.resolve(true);
                }
            });
        });
    }
    else if (error.code === "UNKNOWN") {
        return unknownErrorRetry(filePath, error, stackErr);
    }
    else {
        return PromiseBB.resolve(false);
    }
}
function augmentError(error) {
    if (error.message === "dest already exists.") {
        error.code = "EEXIST";
    }
}
function errorHandler(error, stackErr, tries, showDialogCallback, options) {
    augmentError(error);
    const repProm = errorRepeat(error, error.dest || error.path, tries, stackErr, showDialogCallback, options);
    // trying to narrow down #6404
    if (repProm === undefined) {
        const err = new Error(`Failed to handle filesystem error "${error.code}": ${error.message}.`);
        err.stack = error.stack;
        throw PromiseBB.reject(err);
    }
    return repProm
        .then((repeat) => repeat
        ? PromiseBB.resolve()
        : PromiseBB.reject(restackErr(error, stackErr)))
        .catch((err) => PromiseBB.reject(restackErr(err, stackErr)));
}
export function genFSWrapperAsync(func) {
    const wrapper = (stackErr, tries, ...args) => simfail(() => PromiseBB.resolve(func(...args))).catch((err) => errorHandler(err, stackErr, tries).then(() => wrapper(stackErr, tries - 1, ...args)));
    const res = (...args) => {
        return wrapper(new Error(), NUM_RETRIES, ...args);
    };
    return res;
}
// tslint:disable:max-line-length
const chmodAsync = genFSWrapperAsync(fs.chmod);
const closeAsync = genFSWrapperAsync(fs.close);
const fsyncAsync = genFSWrapperAsync(fs.fsync);
const lstatAsync = genFSWrapperAsync(fs.lstat);
const mkdirAsync = genFSWrapperAsync(fs.mkdir);
const mkdirsAsync = genFSWrapperAsync(fs.mkdirs);
const moveAsync = genFSWrapperAsync(fs.move);
const openAsync = genFSWrapperAsync(fs.open);
const readdirAsync = genFSWrapperAsync(fs.readdir);
const readFileAsync = genFSWrapperAsync(fs.readFile);
const statAsync = genFSWrapperAsync(fs.stat);
const statSilentAsync = (statPath) => PromiseBB.resolve(fs.stat(statPath));
const symlinkAsync = genFSWrapperAsync(fs.symlink);
const utimesAsync = genFSWrapperAsync(fs.utimes);
// fs.write and fs.read don't promisify correctly because it has two return values. fs-extra already works around this in their
// promisified api so no reason to reinvent the wheel (also we want the api to be compatible)
const writeAsync = genFSWrapperAsync(fs.write);
const readAsync = genFSWrapperAsync(fs.read);
const writeFileAsync = genFSWrapperAsync(fs.writeFile);
const appendFileAsync = genFSWrapperAsync(fs.appendFile);
// tslint:enable:max-line-length
export { appendFileAsync, chmodAsync, closeAsync, fsyncAsync, lstatAsync, mkdirAsync, mkdirsAsync, moveAsync, openAsync, readdirAsync, readAsync, readFileAsync, statAsync, statSilentAsync, symlinkAsync, utimesAsync, writeAsync, writeFileAsync, };
export function isDirectoryAsync(dirPath) {
    return PromiseBB.resolve(fs.stat(dirPath)).then((stats) => stats.isDirectory());
}
export function ensureDirSync(dirPath) {
    try {
        fs.ensureDirSync(dirPath);
    }
    catch (err) {
        throw restackErr(err, new Error());
    }
}
export function ensureFileAsync(filePath) {
    const stackErr = new Error();
    return PromiseBB.resolve(fs.ensureFile(filePath)).catch((err) => {
        throw restackErr(err, stackErr);
    });
}
export function ensureDirAsync(dirPath, onDirCreatedCB) {
    const stackErr = new Error();
    // If a onDirCreated callback is provided, we can't use fs-extra's
    //  implementation directly as there's no way for us to reliably determine
    //  whether the parent folder was empty. We're going to create the
    //  directories ourselves.
    return onDirCreatedCB
        ? ensureDir(dirPath, onDirCreatedCB)
        : ensureDirInt(dirPath, stackErr, NUM_RETRIES);
}
function ensureDirInt(dirPath, stackErr, tries) {
    return PromiseBB.resolve(fs.ensureDir(dirPath)).catch((err) => {
        // ensureDir isn't supposed to cause EEXIST errors as far as I understood
        // it but on windows, when targeting a OneDrive path (and similar?)
        // it apparently still does
        if (err.code === "EEXIST") {
            return PromiseBB.resolve();
        }
        return simfail(() => errorHandler(err, stackErr, tries, undefined)).then(() => ensureDirInt(dirPath, stackErr, tries - 1));
    });
}
function ensureDir(targetDir, onDirCreatedCB) {
    // Please note, onDirCreatedCB will be called for _each_ directory
    //  we create.
    const created = [];
    const mkdirRecursive = (dir) => PromiseBB.resolve(fs.mkdir(dir))
        .then(() => {
        created.push(dir);
        return onDirCreatedCB(dir);
    })
        .catch((err) => {
        const code = getErrorCode(err);
        if (code === "EEXIST") {
            return PromiseBB.resolve();
        }
        else {
            return ["ENOENT"].indexOf(code) !== -1
                ? mkdirRecursive(path.dirname(dir))
                    .then(() => PromiseBB.resolve(fs.mkdir(dir)))
                    .then(() => {
                    created.push(dir);
                    return onDirCreatedCB(dir);
                })
                    .catch((err2) => {
                    const code2 = getErrorCode(err2);
                    return code2 === "EEXIST"
                        ? PromiseBB.resolve()
                        : PromiseBB.reject(err2);
                })
                : PromiseBB.reject(err);
        }
    });
    return mkdirRecursive(targetDir).then(() => created.indexOf(targetDir) !== -1
        ? PromiseBB.resolve(targetDir)
        : PromiseBB.resolve(null));
}
function selfCopyCheck(src, dest) {
    return PromiseBB.all([
        fs.stat(src, { bigint: true }),
        fs.stat(dest, { bigint: true }).catch((err) => getErrorCode(err) === "ENOENT"
            ? Promise.resolve({})
            : Promise.reject(err)),
    ]).then((stats) => stats[0].ino === stats[1].ino
        ? PromiseBB.reject(new SelfCopyCheckError(src, dest, stats[0].ino))
        : PromiseBB.resolve());
}
function nextName(input) {
    const ext = path.extname(input);
    const base = path.basename(input, ext);
    const count = parseInt(path.extname(base).slice(1), 10) || 1;
    return path.join(path.dirname(input), `${base}.${count}${ext}`);
}
/**
 * move a file. If the destination exists, will generate a new name with an
 * increasing counter until an unused name is found
 */
export function moveRenameAsync(src, dest) {
    return moveAsync(src, dest, { overwrite: false })
        .then(() => dest)
        .catch({ code: "EEXIST" }, () => moveRenameAsync(src, nextName(dest)));
}
/**
 * copy file
 * The copy function from fs-extra doesn't (at the time of writing) correctly check that a file
 * isn't copied onto itself (it fails for links or potentially on case insensitive disks),
 * so this makes a check based on the ino number.
 * A bug in older versions of node.js made it necessary this check be optional but that is
 * resolved now so the check should always be enabled.
 * @param src file to copy
 * @param dest destination path
 * @param options copy options (see documentation for fs)
 */
export function copyAsync(src, dest, options) {
    const stackErr = new Error();
    // fs.copy in fs-extra has a bug where it doesn't correctly avoid copying files onto themselves
    const check = options?.noSelfCopy
        ? PromiseBB.resolve()
        : selfCopyCheck(src, dest);
    return check
        .then(() => copyInt(src, dest, options || undefined, stackErr, NUM_RETRIES))
        .catch((err) => PromiseBB.reject(restackErr(err, stackErr)));
}
function copyInt(src, dest, options, stackErr, tries) {
    return simfail(() => PromiseBB.resolve(fs.copy(src, dest, options))).catch((err) => errorHandler(err, stackErr, tries, options?.showDialogCallback, {
        extraRetryErrors: ["EEXIST"],
    }).then(() => copyInt(src, dest, options, stackErr, tries - 1)));
}
export function linkAsync(src, dest, options) {
    const stackErr = new Error();
    return linkInt(src, dest, stackErr, NUM_RETRIES, options).catch((err) => PromiseBB.reject(restackErr(err, stackErr)));
}
function linkInt(src, dest, stackErr, tries, options) {
    return simfail(() => PromiseBB.resolve(fs.link(src, dest))).catch((err) => errorHandler(err, stackErr, tries, options !== undefined ? options.showDialogCallback : undefined).then(() => linkInt(src, dest, stackErr, tries - 1, options)));
}
export function removeSync(dirPath) {
    fs.removeSync(dirPath);
}
export function unlinkAsync(filePath, options) {
    return unlinkInt(filePath, new Error(), NUM_RETRIES, options || {});
}
function unlinkInt(filePath, stackErr, tries, options) {
    return simfail(() => PromiseBB.resolve(fs.unlink(filePath))).catch((err) => {
        const handle = () => errorHandler(err, stackErr, tries, options.showDialogCallback).then(() => unlinkInt(filePath, stackErr, tries - 1, options));
        if (err.code === "ENOENT") {
            // don't mind if a file we wanted deleted was already gone
            return PromiseBB.resolve();
        }
        else if (err.code === "EPERM") {
            // this could be caused by the path actually pointing to a directory,
            // unlink can only handle files
            return PromiseBB.resolve(fs.stat(filePath))
                .then((stats) => {
                if (stats.isDirectory()) {
                    err.code = "EISDIR";
                }
                return handle();
            })
                .catch((errInner) => errInner instanceof UserCanceled
                ? Promise.reject(errInner)
                : handle());
        }
        else {
            return handle();
        }
    });
}
export function renameAsync(sourcePath, destinationPath) {
    return renameInt(sourcePath, destinationPath, new Error(), NUM_RETRIES);
}
function renameInt(sourcePath, destinationPath, stackErr, tries) {
    return simfail(() => PromiseBB.resolve(fs.rename(sourcePath, destinationPath))).catch((err) => {
        if (tries > 0 && RETRY_ERRORS.has(err.code)) {
            return PromiseBB.delay((NUM_RETRIES - tries + 1) * RETRY_DELAY_MS).then(() => renameInt(sourcePath, destinationPath, stackErr, tries - 1));
        }
        return err.code === "EPERM"
            ? PromiseBB.resolve(fs.stat(destinationPath))
                .then((stat) => stat.isDirectory()
                ? PromiseBB.reject(restackErr(err, stackErr))
                : errorHandler(err, stackErr, tries).then(() => renameInt(sourcePath, destinationPath, stackErr, tries - 1)))
                .catch((newErr) => PromiseBB.reject(restackErr(newErr, stackErr)))
            : errorHandler(err, stackErr, tries).then(() => renameInt(sourcePath, destinationPath, stackErr, tries - 1));
    });
}
export function rmdirAsync(dirPath) {
    return rmdirInt(dirPath, new Error(), NUM_RETRIES);
}
function rmdirInt(dirPath, stackErr, tries) {
    return simfail(() => PromiseBB.resolve(fs.rmdir(dirPath))).catch((err) => {
        if (err.code === "ENOENT") {
            // don't mind if a file we wanted deleted was already gone
            return PromiseBB.resolve();
        }
        else if (RETRY_ERRORS.has(err.code) && tries > 0) {
            return PromiseBB.delay(RETRY_DELAY_MS).then(() => rmdirInt(dirPath, stackErr, tries - 1));
        }
        throw restackErr(err, stackErr);
    });
}
export function removeAsync(remPath, options) {
    const stackErr = new Error();
    return removeInt(remPath, stackErr, NUM_RETRIES, options || {});
}
function removeInt(remPath, stackErr, tries, options) {
    return simfail(() => rimrafAsync(remPath)).catch((err) => errorHandler(err, stackErr, tries, options.showDialogCallback, {
        enotempty: true,
    }).then(() => removeInt(remPath, stackErr, tries - 1, options)));
}
function rimrafAsync(remPath) {
    return new PromiseBB((resolve, reject) => {
        // don't use the rimraf implementation of busy retries because it's f*cked:
        // https://github.com/isaacs/rimraf/issues/187
        rimraf(remPath, {
            maxBusyTries: 0,
        }, (err) => {
            if (err) {
                reject(err);
            }
            else {
                resolve();
            }
        });
    });
}
export function readlinkAsync(linkPath) {
    const stackErr = new Error();
    return readlinkInt(linkPath, stackErr, NUM_RETRIES);
}
function readlinkInt(linkPath, stackErr, tries) {
    return simfail(() => PromiseBB.resolve(fs.readlink(linkPath))).catch((err) => {
        if (err.code === "UNKNOWN" && process.platform === "win32") {
            // on windows this return UNKNOWN if the file is not a link.
            // of course there could be a thousand other things returning UNKNOWN but we'll never
            // know, will we? libuv? will we?
            const newErr = new Error("Not a link");
            newErr.code = "EINVAL";
            newErr.syscall = "readlink";
            newErr.path = linkPath;
            return Promise.reject(newErr);
        }
        else if (err.code === "EINVAL") {
            return Promise.reject(err);
        }
        else {
            return errorHandler(err, stackErr, tries).then(() => readlinkInt(linkPath, stackErr, tries - 1));
        }
    });
}
const ELEVATED_TIMEOUT_MS = 30000;
function elevated(func, parameters) {
    let server;
    return new PromiseBB((resolve, reject) => {
        const id = shortid();
        let resolved = false;
        let elevatedError;
        const ipcPath = `__fs_elevated_${id}`;
        const timeout = setTimeout(() => {
            if (!resolved) {
                resolved = true;
                reject(new Error("Elevated process did not connect within the expected time. "
                    + "This usually indicates it crashed or failed to start."));
            }
        }, ELEVATED_TIMEOUT_MS);
        server = net
            .createServer((connRaw) => {
            clearTimeout(timeout);
            const conn = new JsonSocket(connRaw);
            conn
                .on("message", (data) => {
                if (data.error !== undefined) {
                    log("error", "elevated process failed", data.error);
                    elevatedError = data.error;
                }
                else {
                    log("warn", "got unexpected ipc message", JSON.stringify(data));
                }
            })
                .on("end", () => {
                if (!resolved) {
                    resolved = true;
                    if (elevatedError !== undefined) {
                        reject(new Error(elevatedError));
                    }
                    else {
                        resolve();
                    }
                }
            })
                .on("error", (err) => {
                log("error", "elevated code reported error", err);
                if (!resolved) {
                    resolved = true;
                    reject(err);
                }
            });
        })
            .listen(path.join("\\\\?\\pipe", ipcPath));
        runElevated(ipcPath, func, parameters).catch((err) => {
            clearTimeout(timeout);
            const nativeCode = getErrorNativeCode(err);
            const error = unknownToError(err);
            if (getErrorCode(err) === "5" ||
                (process.platform === "win32" && nativeCode === 1223)) {
                // this code is returned when the user rejected the UAC dialog. Not currently
                // aware of another case
                reject(new UserCanceled());
            }
            else {
                reject(new Error(`OS error ${error.message} (${getErrorCode(err) ?? nativeCode})`));
            }
        });
    }).finally(() => {
        if (server !== undefined) {
            server.close();
        }
    });
}
export function ensureDirWritableAsync(dirPath, confirm) {
    if (confirm === undefined) {
        confirm = () => PromiseBB.resolve();
    }
    const stackErr = new Error();
    return PromiseBB.resolve(fs.ensureDir(dirPath))
        .then(() => {
        const canary = path.join(dirPath, "__vortex_canary");
        return ensureFileAsync(canary).then(() => removeAsync(canary));
    })
        .catch((err) => {
        // weirdly we get EBADF from ensureFile sometimes when the
        // directory isn't writeable instead of EPERM. More weirdly, this seems to happen
        // only on startup.
        // Additionally, users may occasionally get EEXIST (OneDrive specific?)
        //  as far as I understand fs-extra that is not supposed to happen! but I suppose
        //  it doesn't hurt to add some code to handle that use case.
        //  https://github.com/Nexus-Mods/Vortex/issues/6856
        if (["EPERM", "EBADF", "UNKNOWN", "EEXIST"].indexOf(err.code) !== -1) {
            return PromiseBB.resolve(confirm()).then(() => {
                const userId = permission.getUserId();
                return (elevated((ipcPath, req) => {
                    // tslint:disable-next-line:no-shadowed-variable
                    const fs = req("fs-extra");
                    // tslint:disable-next-line:no-shadowed-variable
                    const path = req("path");
                    const { allow } = req("permissions");
                    const allowDir = (targetPath) => {
                        try {
                            allow(targetPath, userId, "rwx");
                            return Promise.resolve();
                        }
                        catch (err) {
                            return Promise.reject(err);
                        }
                    };
                    // recurse upwards in the directory tree if necessary
                    const ensureAndAllow = (targetPath, allowRecurse) => {
                        return fs
                            .ensureDir(targetPath)
                            .catch((elevatedErr) => {
                            const parentPath = path.dirname(targetPath);
                            if (["EPERM", "ENOENT"].includes(elevatedErr.code) &&
                                parentPath !== targetPath &&
                                allowRecurse) {
                                return ensureAndAllow(parentPath, true).then(() => ensureAndAllow(targetPath, false));
                            }
                            else if (elevatedErr.code === "EEXIST") {
                                // Directory already exists - that's fine.
                                //  Theoretically fs.ensureDir shouldn't be throwing EEXIST
                                //  errors, but we've seen this happen on multiple occassions.
                                return Promise.resolve();
                            }
                            else {
                                return Promise.reject(elevatedErr);
                            }
                        })
                            .then(() => allowDir(targetPath));
                    };
                    return ensureAndAllow(dirPath, true);
                }, { dirPath, userId })
                    // if elevation fails, rethrow the original error, not the failure to elevate
                    .catch((elevatedErr) => {
                    if (elevatedErr.message.indexOf("The operation was canceled by the user") !== -1) {
                        return Promise.reject(new UserCanceled());
                    }
                    // if elevation failed, return the original error because the one from
                    // elevate, while interesting as well, would make error handling too complicated
                    log("error", "failed to acquire permission", elevatedErr.message);
                    return PromiseBB.reject(restackErr(err, stackErr));
                }));
            });
        }
        else {
            return PromiseBB.reject(restackErr(err, stackErr));
        }
    });
}
export function changeFileOwnership(filePath, stat) {
    if (process.platform === "win32") {
        // This is a *nix only function.
        return PromiseBB.resolve();
    }
    const readAndWriteOther = parseInt("0006", 8);
    if ((stat.mode & readAndWriteOther) === readAndWriteOther) {
        return PromiseBB.reject(new ProcessCanceled("Ownership change not required"));
    }
    const readAndWriteGroup = parseInt("0060", 8);
    const hasGroupPermissions = (stat.mode & readAndWriteGroup) === readAndWriteGroup;
    // (Writing this down as it can get confusing) Cases where we need to change ownership are:
    //  <BaseOwnerCheck> - If the process real ID is different than the file's real ID.
    //
    //  1. If <BaseOwnerCheck> is true and the file does NOT have the group read/write bits set.
    //  2. If <BaseOwnerCheck> is true and the file DOES have the group read/write bits set but
    //   the process group id differs from the file's group id.
    //
    // Ask for forgiveness, not permission.
    return stat.uid !== process.getuid()
        ? !hasGroupPermissions ||
            (hasGroupPermissions && stat.gid !== process.getgid())
            ? PromiseBB.resolve(fs.chown(filePath, process.getuid(), stat.gid)).catch((err) => PromiseBB.reject(err))
            : PromiseBB.resolve()
        : PromiseBB.resolve();
}
export function changeFileAttributes(filePath, wantedAttributes, stat) {
    return changeFileOwnership(filePath, stat)
        .then(() => {
        const finalAttributes = stat.mode | wantedAttributes;
        return PromiseBB.resolve(fs.chmod(filePath, finalAttributes));
    })
        .catch(ProcessCanceled, () => PromiseBB.resolve())
        .catch((err) => PromiseBB.reject(err));
}
export function makeFileWritableAsync(filePath) {
    const stackErr = new Error();
    const wantedAttributes = process.platform === "win32" ? parseInt("0666", 8) : parseInt("0600", 8);
    return PromiseBB.resolve(fs.stat(filePath)).then((stat) => {
        if (!stat.isFile()) {
            const err = new Error(`Expected a file, found a directory: "${filePath}"`);
            err.code = "EISDIR";
            err.path = filePath;
            err.syscall = "stat";
            err.stack = stackErr.stack;
            return PromiseBB.reject(err);
        }
        return (stat.mode & wantedAttributes) !== wantedAttributes
            ? changeFileAttributes(filePath, wantedAttributes, stat)
            : PromiseBB.resolve();
    });
}
function raiseUACDialog(t, err, op, filePath) {
    let fileToAccess = filePath !== undefined ? filePath : err.path;
    const options = {
        title: "Access denied (2)",
        message: t('Vortex needs to access "{{ fileName }}" but doesn\'t have permission to.\n' +
            "If your account has admin rights Vortex can unlock the file for you. " +
            "Windows will show an UAC dialog.", { replace: { fileName: fileToAccess } }),
        buttons: ["Cancel", "Retry", "Give permission"],
        noLink: true,
        type: "warning",
    };
    return PromiseBB.resolve(showMessageBox(options)).then((result) => {
        const choice = result.response;
        if (choice === 1) {
            // Retry
            return forcePerm(t, op, filePath);
        }
        else if (choice === 2) {
            // Give Permission
            const userId = permission.getUserId();
            return PromiseBB.resolve(fs.stat(fileToAccess))
                .catch((statErr) => {
                if (statErr.code === "ENOENT") {
                    fileToAccess = path.dirname(fileToAccess);
                }
                return PromiseBB.resolve();
            })
                .then(() => elevated((ipcPath, req) => {
                // tslint:disable-next-line:no-shadowed-variable
                const { allow } = req("permissions");
                return allow(fileToAccess, userId, "rwx");
            }, { fileToAccess, userId }).catch((elevatedErr) => {
                if (elevatedErr instanceof UserCanceled ||
                    elevatedErr.message.indexOf("The operation was canceled by the user") !== -1) {
                    return Promise.reject(new UserCanceled());
                }
                // if elevation failed, return the original error because the one from
                // elevate, while interesting as well, would make error handling too complicated
                log("error", "failed to acquire permission", elevatedErr.message);
                return Promise.reject(err);
            }))
                .then(() => forcePerm(t, op, filePath));
        }
        else {
            return PromiseBB.reject(new UserCanceled());
        }
    });
}
export function forcePerm(t, op, filePath, maxTries = 3) {
    return op().catch((err) => {
        const fileToAccess = filePath !== undefined ? filePath : err.path;
        if (["EPERM", "EACCES"].indexOf(err.code) !== -1 ||
            (isErrorWithSystemCode(err) && err.systemCode === 5)) {
            const wantedAttributes = process.platform === "win32"
                ? parseInt("0666", 8)
                : parseInt("0600", 8);
            return fs
                .stat(fileToAccess)
                .then((stat) => changeFileAttributes(fileToAccess, wantedAttributes, stat))
                .then(() => op())
                .catch((innerErr) => {
                if (innerErr instanceof UserCanceled) {
                    return Promise.resolve(undefined);
                }
                return raiseUACDialog(t, err, op, filePath);
            });
        }
        else if (RETRY_ERRORS.has(err.code) && maxTries > 0) {
            return PromiseBB.delay(RETRY_DELAY_MS).then(() => forcePerm(t, op, filePath, maxTries - 1));
        }
        else {
            return PromiseBB.reject(err);
        }
    });
}
export function withTmpDirImpl(cb) {
    return new PromiseBB((resolve, reject) => {
        tmp.dir({ unsafeCleanup: true }, (err, tmpPath, cleanup) => {
            if (err !== null) {
                return reject(err);
            }
            else {
                cb(tmpPath)
                    .then((out) => {
                    resolve(out);
                })
                    .catch((tmpErr) => {
                    reject(tmpErr);
                })
                    .finally(() => {
                    try {
                        cleanup();
                    }
                    catch (err) {
                        // cleanup failed
                        log("warn", "Failed to clean up temporary directory", {
                            tmpPath,
                        });
                    }
                });
            }
        });
    });
}
function withTmpFileImpl(cb, options) {
    return new PromiseBB((resolve, reject) => {
        tmp.file(_.omit(options ?? {}, ["cleanup"]), (err, name, fd, cleanup) => {
            if (err !== null) {
                return reject(err);
            }
            else {
                cb(fd, name)
                    .then(resolve)
                    .catch(reject)
                    .finally(() => {
                    if (options?.cleanup !== false) {
                        try {
                            cleanup();
                        }
                        catch (err) {
                            log("warn", "Failed to clean up temporary file", { name });
                        }
                    }
                });
            }
        });
    });
}
const withTmpDir = genFSWrapperAsync(withTmpDirImpl);
const withTmpFile = genFSWrapperAsync(withTmpFileImpl);
export { withTmpDir, withTmpFile };
const KNOWN_BOMS = [
    { bom: Buffer.from([0xef, 0xbb, 0xbf]), enc: "utf8" },
    { bom: Buffer.from([0x00, 0x00, 0xfe, 0xff]), enc: "utf32-be" },
    { bom: Buffer.from([0xff, 0xfe, 0x00, 0x00]), enc: "utf32-le" },
    { bom: Buffer.from([0xfe, 0xff]), enc: "utf16be" },
    { bom: Buffer.from([0xff, 0xfe]), enc: "utf16le" },
];
export function encodingFromBOM(buf) {
    const bom = KNOWN_BOMS.find((b) => b.bom.length < buf.length && b.bom.compare(buf, 0, b.bom.length) === 0);
    if (bom !== undefined) {
        return { encoding: bom.enc, length: bom.bom.length };
    }
    return undefined;
}
/**
 * read file, using the BOM to determine the encoding
 * @param filePath the file to read
 * @param fallbackEncoding the encoding to use if there is no BOM. Expects one of the iconv-constants,
 *                         which seem to be a super-set of the regular node buffer encodings
 * @returns decoded file encoding
 */
export function readFileBOM(filePath, fallbackEncoding) {
    return Promise.resolve(readFileAsync(filePath)).then((buffer) => {
        // iconv-lite has its own BOM handling but it's weird because you apparently
        // still have to specify utf-8/utf-16/utf-32 - it just detects the endianness
        const detectedEnc = encodingFromBOM(buffer);
        if (detectedEnc === undefined) {
            // no bom
            return decode(buffer, fallbackEncoding ?? "utf8");
        }
        else {
            return decode(buffer.slice(detectedEnc.length), detectedEnc?.encoding ?? fallbackEncoding);
        }
    });
}
